/**
 * Social-media links (Footer + Article sidebar) — editable from the dashboard.
 *
 * Stored locally for instant updates, with an optional publish-to-GitHub
 * helper that writes `/content/social.json` so the change becomes global
 * after the next deploy.
 */
import { useEffect, useState } from "react";

export type SocialKey =
  | "facebook"
  | "instagram"
  | "x"
  | "telegram"
  | "whatsapp"
  | "youtube"
  | "github";

export type SocialLinks = Partial<Record<SocialKey, string>>;

const KEY = "rawan-social-links-v1";

export const DEFAULT_SOCIAL_LINKS: SocialLinks = {
  facebook: "https://facebook.com/",
  instagram: "https://instagram.com/",
  x: "https://x.com/",
  telegram: "https://t.me/",
  whatsapp: "https://wa.me/",
  youtube: "",
  github: "",
};

/** Optional file shipped under /content/social.json (loaded on boot). */
const contentSocialRaw = (
  import.meta.glob("/content/social.json", { eager: true, import: "default" }) as Record<
    string,
    unknown
  >
)["/content/social.json"] as SocialLinks | undefined;

type Listener = () => void;
const listeners = new Set<Listener>();

function read(): SocialLinks {
  if (typeof window === "undefined")
    return { ...DEFAULT_SOCIAL_LINKS, ...(contentSocialRaw ?? {}) };
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULT_SOCIAL_LINKS, ...(contentSocialRaw ?? {}) };
    return { ...DEFAULT_SOCIAL_LINKS, ...(contentSocialRaw ?? {}), ...(JSON.parse(raw) as SocialLinks) };
  } catch {
    return { ...DEFAULT_SOCIAL_LINKS, ...(contentSocialRaw ?? {}) };
  }
}

export function getSocialLinks(): SocialLinks {
  return read();
}

export function setSocialLinks(next: SocialLinks) {
  const merged = { ...read(), ...next };
  // Drop empty strings so defaults can surface.
  (Object.keys(merged) as SocialKey[]).forEach((k) => {
    if (!merged[k]) delete merged[k];
  });
  localStorage.setItem(KEY, JSON.stringify(merged));
  listeners.forEach((l) => l());
}

export function useSocialLinks(): [SocialLinks, (n: SocialLinks) => void] {
  const [v, setV] = useState<SocialLinks>(() => read());
  useEffect(() => {
    const l: Listener = () => setV(read());
    listeners.add(l);
    return () => { listeners.delete(l); };
  }, []);
  return [v, setSocialLinks];
}

export const SOCIAL_REMOTE_PATH = "social.json";